// Shared store for push notifications — PushNotificationListener.jsx writes
// to it, Header's NotificationBell reads from it. Backed by localStorage
// (same persistence convention as the rest of the app) plus a window
// CustomEvent so the bell updates live without Redux, since Header remounts
// on every page anyway (no shared layout).
const STORAGE_KEY = "fcm_notifications";
const MAX_STORED = 50;
const NOTIFICATIONS_EVENT = "fcm-notifications-changed";

// Messages that arrive while the tab is hidden/closed go to
// public/firebase-messaging-sw.js instead of onMessage. The service worker
// has no localStorage, so it queues them in IndexedDB and we move them into
// the store from here. Keep these in sync with the service worker.
const PENDING_DB_NAME = "ksdpl-fcm";
const PENDING_STORE = "pending";
export const BACKGROUND_MESSAGE_TYPE = "fcm-background-message";

export function getStoredNotifications() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY)) || [];
  } catch {
    return [];
  }
}

function persist(notifications) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(notifications));
  window.dispatchEvent(
    new CustomEvent(NOTIFICATIONS_EVENT, { detail: notifications })
  );
  return notifications;
}

function toNotification(payload) {
  return {
    id: crypto.randomUUID(),
    title: payload.notification?.title || payload.data?.title || "New Notification",
    body: payload.notification?.body || payload.data?.body || "",
    receivedAt: payload.receivedAt || Date.now(),
    read: false,
  };
}

export function addNotification(payload) {
  return persist([toNotification(payload), ...getStoredNotifications()].slice(0, MAX_STORED));
}

export function markAllNotificationsRead() {
  return persist(getStoredNotifications().map((n) => ({ ...n, read: true })));
}

export function setNotificationRead(id, read) {
  return persist(getStoredNotifications().map((n) => (n.id === id ? { ...n, read } : n)));
}

export function clearNotifications() {
  return persist([]);
}

// Reads and empties the service worker's queue in one transaction, so two
// open tabs syncing at once can't both add the same message.
function takePendingBackgroundMessages() {
  return new Promise((resolve) => {
    const request = indexedDB.open(PENDING_DB_NAME, 1);
    request.onupgradeneeded = () =>
      request.result.createObjectStore(PENDING_STORE, { autoIncrement: true });
    request.onerror = () => resolve([]);
    request.onsuccess = () => {
      const db = request.result;
      const tx = db.transaction(PENDING_STORE, "readwrite");
      const store = tx.objectStore(PENDING_STORE);
      const getAll = store.getAll();
      store.clear();
      tx.oncomplete = () => {
        db.close();
        resolve(getAll.result || []);
      };
      tx.onerror = () => {
        db.close();
        resolve([]);
      };
    };
  });
}

export async function syncBackgroundNotifications() {
  const pending = await takePendingBackgroundMessages();
  if (pending.length === 0) return;

  const incoming = pending
    .map(toNotification)
    .sort((a, b) => b.receivedAt - a.receivedAt);
  persist([...incoming, ...getStoredNotifications()].slice(0, MAX_STORED));
}

export function subscribeToNotifications(callback) {
  const handler = (event) => callback(event.detail);
  // `storage` fires when another open tab of the app changes the store.
  const storageHandler = (event) => {
    if (event.key === STORAGE_KEY) callback(getStoredNotifications());
  };
  window.addEventListener(NOTIFICATIONS_EVENT, handler);
  window.addEventListener("storage", storageHandler);
  return () => {
    window.removeEventListener(NOTIFICATIONS_EVENT, handler);
    window.removeEventListener("storage", storageHandler);
  };
}
